'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { AlertTriangle, Clock, X } from 'lucide-react';
import { useTenantSubscription } from '@/components/subscription/TenantSubscriptionContext';

/**
 * Global trial-awareness banner. Sits at the top of every admin route below
 * the AdminViewAsDropdown topbar. Three escalation tiers driven by
 * `trialDaysRemaining`:
 *
 *   - Day 8+        : nothing (don't nag too early)
 *   - Day 3-7       : amber slim banner — soft nudge
 *   - Day 0-2       : rose slim banner — last chance
 *   - Expired       : rose solid banner with mandatory CTA (not dismissable)
 *
 * Dismissable per-session via sessionStorage (banner reappears on next
 * login). Hides itself on /admin/subscription* since the page already
 * shows a richer banner inline.
 */
export function TrialAwarenessBanner() {
  const pathname = usePathname();
  const t = useTranslations('admin.subscription.awareness');
  const { currentSubscription, trialDaysRemaining, isTrialExpired, subscriptionLoading, bypass } =
    useTenantSubscription();
  const [dismissed, setDismissed] = useState(false);

  // Restore dismissed-state from sessionStorage on mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const todayKey = `trial-banner-dismissed-${new Date().toDateString()}`;
    setDismissed(sessionStorage.getItem(todayKey) === '1');
  }, []);

  // Hide on subscription pages — they have their own richer banner inline.
  if (pathname?.startsWith('/admin/subscription')) return null;
  if (subscriptionLoading) return null;
  if (bypass) return null; // Bypass-Tenants haben keine Trial-Begrenzung
  if (!currentSubscription) return null;
  if (currentSubscription.status !== 'trial' && currentSubscription.status !== 'past_due') return null;

  // F-500 2026: when the tenant has already entered payment data during
  // trial (Stripe keep-trial flow → stripe_subscription_id set), they've
  // committed — there is literally nothing they need to do before the
  // trial ends. Loud "trial ends in X days, upgrade NOW" banners on every
  // page would be anxiety-inducing and undermine trust. Linear / Stripe
  // Dashboard pattern: silence the urgency, the inline PlanHero shows the
  // calm "✓ Zahlung hinterlegt" confirmation instead.
  if (
    currentSubscription.status === 'trial' &&
    (currentSubscription as { stripe_subscription_id?: string | null }).stripe_subscription_id
  ) {
    return null;
  }

  // Don't render if user is still comfortably inside their trial.
  if (!isTrialExpired && trialDaysRemaining > 7) return null;
  if (dismissed && !isTrialExpired) return null;

  const tier = isTrialExpired || trialDaysRemaining <= 0
    ? 'expired'
    : trialDaysRemaining <= 2
      ? 'danger'
      : 'warning';

  const config = {
    warning: {
      icon: Clock,
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-900',
      iconClass: 'text-amber-600',
      dismissable: true,
    },
    danger: {
      icon: AlertTriangle,
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      text: 'text-rose-900',
      iconClass: 'text-rose-600',
      dismissable: true,
    },
    expired: {
      icon: AlertTriangle,
      bg: 'bg-rose-50',
      border: 'border-rose-300',
      text: 'text-rose-900',
      iconClass: 'text-rose-700',
      dismissable: false,
    },
  } as const;

  const { icon: Icon, bg, border, text, iconClass, dismissable } = config[tier];

  const handleDismiss = () => {
    setDismissed(true);
    if (typeof window !== 'undefined') {
      const todayKey = `trial-banner-dismissed-${new Date().toDateString()}`;
      sessionStorage.setItem(todayKey, '1');
    }
  };

  const message =
    tier === 'expired'
      ? t('globalBannerExpired')
      : tier === 'danger'
        ? t('globalBannerDanger', { days: Math.max(0, trialDaysRemaining) })
        : t('globalBannerWarning', { days: trialDaysRemaining });

  return (
    <div className={`flex items-center gap-3 border-b px-4 py-2.5 sm:px-6 ${bg} ${border}`}>
      <Icon className={`h-4 w-4 flex-shrink-0 ${iconClass}`} strokeWidth={2} />
      <p className={`flex-1 truncate text-sm font-medium ${text}`}>{message}</p>
      <Link
        href="/admin/subscription"
        className={`flex-shrink-0 text-sm font-semibold underline-offset-4 hover:underline ${text}`}
      >
        {tier === 'expired' ? t('globalBannerCtaReactivate') : t('globalBannerCtaActivate')}
      </Link>
      {dismissable && (
        <button
          type="button"
          onClick={handleDismiss}
          className={`flex-shrink-0 rounded p-1 transition-colors hover:bg-black/5 ${text}`}
          aria-label={t('globalBannerDismiss')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
